/**
 * Checks if a value is numeric (number or numeric string).
 * @param {*} value
 * @returns {Boolean}
 */
function isNumeric(value) {
	return (
		(typeof value === 'number' && !isNaN(value)) ||
		(typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value)))
	);
}

/* Notification module */
export var notifier = (function () {
	var onNotification = null; // Notification handler
	var onServerLog = null; // Log Write Handler
	//var notification_handlers = []; // List of notification handlers

	var notify_errors = true; // Whether to show notifications on errors

	/**
	 * Display notification
	 * @param {String|Number} level Notification level: debug, [info | notice], [warning | attention], error, [critical | fatal] or a numerical value from notifier.notification_level
	 * @param {String} text         Notification text
	 * @param {String} [title]      Notification Title Text
	 * @param {Boolean} [log]       Flag to add to log
	 */
	var notify = function (level, text, title = '', consoleLog = false, serverLog = false) {
		if (!text) return;

		level = getNumericLevel(level);

		// If the handler is not installed
		// If logging is enabled
		if (!onNotification || consoleLog) {
			if (!onNotification) console.warn('Notification handler not installed');
			if (consoleLog) {
				logToConsole(level, `Notification: ${title ? `|${title}| ` : ''}${text}`, serverLog);
			} else if (serverLog) {
				logToServer(level, `Notification: ${title ? `|${title}| ` : ''}${text}`);
			}

			if (level === notifier.level.debug || level === notifier.level.info || level === notifier.level.success) {
				console.log(`Notification: ${text}`);
				onServerLog?.('info', `Notification: ${text}`);
			} else if (level === notifier.level.warning) {
				console.warn(`Notification: ${text}`);
				onServerLog?.('warning', `Notification: ${text}`);
			} else if (level === notifier.level.error || level === notifier.level.critical) {
				console.error(`Notification: ${text}`);
				onServerLog?.('error', `Notification: ${text}`);
			}
		}
		onNotification?.(level, text, title);
	};

	/**
	 * Add log entry to console
	 * @param {String|Number} level Message level: debug, [info | notice], [warning | attention], error, [critical | fatal] or a numerical value from notifier.notification_level
	 * @param {String} text         Message text
	 * @param {Boolean} serverLog   Flag for sending log to server
	 */
	var logToConsole = function (level, text, serverLog = false) {
		// If the message text is not set
		if (!text) return;
		// Get the message level as a number
		level = getNumericLevel(level);
		// Write to the console using a method depending on the message level
		switch (level) {
			case notifier.level.debug:
				console.log(text);
				break;
			case notifier.level.info:
			case notifier.level.success:
				console.info(text);
				break;
			case notifier.level.warning:
				console.warn(text);
				break;
			case notifier.level.error:
				console.error(text);
				break;
			default:
				break;
		}
		// If logging to the server is enabled, then we start it
		if (serverLog) logToServer(level, text);
	};

	/**
	 * Send log entry to server
	 * @param {String|Number} level Notification level
	 * @param {String} text         Message text
	 */
	var logToServer = function (level, text) {
		// If the message text is not set
		if (!text) return;
		// If a logging handler to the server is set
		if (onServerLog) {
			// Get the notification level as a number
			level = getNumericLevel(level);
			// Send the log record to the server
			onServerLog(level, text);
		}
	};

	/**
	 * Get the numerical value of the notification level
	 * @param {String|Number} level Notification level
	 * @returns {Number}            Notification level in the form of notifier.notification_level
	 */
	var getNumericLevel = function (level) {
		// Numerical level, so the level is already set
		if (isNumeric(level)) return level;
		// Return the corresponding level number
		if (typeof level === 'string') {
			switch (level) {
				case 'debug':
					return notifier.level.debug;
				case 'info':
				case 'notice':
					return notifier.level.info;
				case 'warning':
				case 'attention':
					return notifier.level.warning;
				case 'error':
					return notifier.level.error;
				case 'critical':
				case 'fatal':
					return notifier.level.critical;
				default:
					break;
			}
		}
		// Level is not set, return default value
		if (typeof level === 'undefined') return notifier.level.info;
		// Incorrect notification level set
		console.warn('Incorrect logging level set (use INFO level):', level);
		return notifier.level.info;
	};

	/**
	 * Return the generated text representation of the current function
	 * @param {Object[]} args Argument list
	 */
	var getFunctionString = function (fname, args) {
		/*let fname = ""
        try {
            let error = new Error('test')
            console.log('Error: ', error)
            fname = error.stack.match(/at (\S+)/g)[1] // Select the next-to-last function from the stack
        }
        catch (e) {
            console.exception(e)
        }*/
		//fname.replace('at Object.', '').replace('at ', '') // delete "at Object." or "at "
		// Adding arguments
		if (Array.isArray(args)) return `${fname}(${args.join(', ')})`;
		else {
			console.warn(`Unable to output function arguments ${fname}: make sure array of arguments is passed`);
			return `${fname}(...)`;
		}
	};

	/**
	 * Add an error to the log with the specified text and the function where the error occurred
	 * @param {String} text         Error description text
	 * @param {String} caller       Text representation of the function in which the error occurred
	 * @param {String} [prefix]     Text before the error description
	 * @param {String} [title]      Error header
	 * @param {Boolean} [suppress]  Flag to disable pop-up notification (logging only)
	 */
	var showError = function (text, caller, prefix = 'Error: ', title = '', suppress = false) {
		let log_msg = `${prefix}${title}\n${text.charAt(0).toLowerCase()}${text.slice(1)}`;
		logToConsole(notifier.level.error, log_msg, true);
		if (caller) logToConsole(notifier.level.info, `Function ${caller}`, true);
		// Display error notification if specified
		if (notify_errors && !suppress) notifier.notify(notifier.level.error, text, title, false, false);
	};

	/**
	 * Set up a notification handler. Handler function should request (level, message, title)
	 * @param {Function} handler Notification handling function
	 */
	var setNotificationHandler = function (handler) {
		onNotification = handler;
	};

	var setServerLogHandler = function (handler) {
		onServerLog = handler;
	};

	return {
		level: {
			debug: 0,
			info: 1,
			success: 2,
			warning: 3,
			error: 4,
			critical: 5,
		},

		notify: notify,
		logToConsole: logToConsole,
		logToServer: logToServer,

		getFunctionString: getFunctionString,
		showError: showError,

		// Setting external notification handlers
		setNotificationHandler: setNotificationHandler,
		setServerLogHandler: setServerLogHandler,
	};
})();
